// frontend/src/services/scheduleAuditFixtures.js
//
// Dev-mode inline fixtures for SCHED-SLICE1-F audit trail viewer.
//
// Gated by `localStorage.SCHED_AUDIT_DEV_FIXTURES === 'true'` — pure-additive,
// no prod impact. Used while Simone's SCHED-SLICE1-A read-side endpoint
// `GET /api/workforce/schedule/audit` is being authored.
//
// Once the live endpoint ships, set the localStorage key to anything else
// (or remove it) and the fixture path is bypassed in api.js.

const FIXTURE_STAFF = {
  staff_jamie: "Jamie Smith",
  staff_alex: "Alex Rivera",
  staff_sarah: "Sarah Chen",
};

const FIXTURE_USERS = {
  user_imani: "Imani Banks",
  user_marcus: "Marcus Bell",
};

const baseWeek = "2026-04-19"; // Sun

/**
 * Return a deterministic fixture set for a given weekStart/shiftId.
 *
 * Designed to exercise every visible state in the audit viewer:
 *   - shift_created
 *   - shift_edited (with full before/after diff)
 *   - shift_deleted
 *   - shift_published (single)
 *   - schedule_published (bulk)
 *   - shift_cancelled
 *   - empty state (when shiftId === 'empty')
 */
export const getScheduleAuditFixture = ({ weekStart, shiftId } = {}) => {
  if (shiftId === "empty") {
    return { auditRows: [], staffNameById: FIXTURE_STAFF, userNameById: FIXTURE_USERS };
  }

  const allRows = [
    {
      _id: "audit_001",
      action: "shift_created",
      category: "workforce",
      createdAt: "2026-04-18T09:00:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_jamie_tue",
        staffProfileId: "staff_jamie",
        startTime: "2026-04-21T07:00:00-07:00",
        endTime: "2026-04-21T14:00:00-07:00",
        roleCode: "front_desk",
        status: "draft",
      },
    },
    {
      _id: "audit_002",
      action: "shift_published",
      category: "workforce",
      createdAt: "2026-04-18T14:15:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_jamie_tue",
        staffProfileId: "staff_jamie",
        publishedAt: "2026-04-18T14:15:00-07:00",
      },
    },
    {
      _id: "audit_003",
      action: "shift_edited",
      category: "workforce",
      createdAt: "2026-04-19T10:32:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_jamie_tue",
        before: {
          startTime: "2026-04-21T07:00:00-07:00",
          endTime: "2026-04-21T14:00:00-07:00",
          staffProfileId: "staff_jamie",
          roleCode: "front_desk",
          status: "published",
        },
        after: {
          startTime: "2026-04-21T08:00:00-07:00",
          endTime: "2026-04-21T14:00:00-07:00",
          staffProfileId: "staff_jamie",
          roleCode: "front_desk",
          status: "published",
        },
      },
    },
    {
      _id: "audit_004",
      action: "shift_edited",
      category: "workforce",
      createdAt: "2026-04-19T11:45:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_jamie_tue",
        before: {
          startTime: "2026-04-21T08:00:00-07:00",
          endTime: "2026-04-21T14:00:00-07:00",
          staffProfileId: "staff_jamie",
          roleCode: "front_desk",
          status: "published",
        },
        after: {
          startTime: "2026-04-21T08:00:00-07:00",
          endTime: "2026-04-21T14:00:00-07:00",
          staffProfileId: "staff_alex",
          roleCode: "front_desk",
          status: "published",
        },
      },
    },
    {
      _id: "audit_005",
      action: "shift_deleted",
      category: "workforce",
      createdAt: "2026-04-19T13:00:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_alex_wed_draft",
        before: {
          startTime: "2026-04-22T09:00:00-07:00",
          endTime: "2026-04-22T15:00:00-07:00",
          staffProfileId: "staff_alex",
          roleCode: "front_desk",
          status: "draft",
        },
      },
    },
    {
      _id: "audit_006",
      action: "schedule_published",
      category: "workforce",
      createdAt: "2026-04-18T17:00:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftIds: ["shift_a", "shift_b", "shift_c", "shift_d", "shift_e"],
        publishedAt: "2026-04-18T17:00:00-07:00",
        dateRange: { from: "2026-04-19", to: "2026-04-25" },
      },
    },
    {
      _id: "audit_007",
      action: "shift_cancelled",
      category: "workforce",
      createdAt: "2026-04-20T08:30:00-07:00",
      createdBy: "user_imani",
      actorName: "Imani",
      metadata: {
        shiftId: "shift_sarah_mon",
        cancelledAt: "2026-04-20T08:30:00-07:00",
        reason: "Staff illness — no replacement available.",
      },
    },
  ];

  // Filter for per-shift case.
  if (shiftId) {
    return {
      auditRows: allRows.filter((r) => {
        const m = r.metadata || {};
        if (m.shiftId === shiftId) return true;
        if (Array.isArray(m.shiftIds) && m.shiftIds.includes(shiftId)) return true;
        return false;
      }),
      staffNameById: FIXTURE_STAFF,
      userNameById: FIXTURE_USERS,
    };
  }

  // Per-week view — return all.
  // (Real BE filters by weekStart against `metadata.startTime` or
  // `metadata.publishedAt`; fixture returns everything regardless of weekStart
  // so dev devs always see content.)
  void weekStart;
  void baseWeek;
  return {
    auditRows: allRows,
    staffNameById: FIXTURE_STAFF,
    userNameById: FIXTURE_USERS,
  };
};
